import { ISOString } from "./date";
import type { EndorsementConfigMap } from "./endorsementConfig";

// ─────────────────────────────────────────────────────────────
// Unified endorsement type
// ─────────────────────────────────────────────────────────────

export type EndorsementType =
  | "deductible_modification"
  | "family_deductible"
  | "tiered_deductible"
  | "aggregate_accommodation"
  | "experience_credit"
  | "experience_refund"
  | "rate_caps"
  | "no_new_lasers"
  | "advance_funding"
  | "admin_changes"
  | "leave_of_absence"
  | "amendments"
  | "provider_dispute_credit"
  | "plan_mirroring"
  | "retained_corridor"
  | "specific_terminal_liability"
  | "aggregate_terminal_liability"
  | "contingent_terminal_liability"
  | "clinical_trials"
  | "transplant_coverage"
  | "travel_medical"
  | "reference_based_pricing"
  | "specialty_drug"
  | "exclusions"
  | "domestic_reimbursement_adjustments";

/**
 * Qualifying medical codes attached to an endorsement (e.g., deductible modifications).
 * Maps to junction tables: deductible_mod_icd_codes, deductible_mod_cpt_codes
 */
export interface EndorsementCodes {
  icd?: string[];
  cpt?: string[];
}

/**
 * Qualifying condition enum values from deductible_mod_qualifying_conditions.
 * Maps to: deductible_mod_qualifying_conditions.condition column.
 */
export type QualifyingCondition =
  | "kidney_failure"
  | "cancer"
  | "transplant"
  | "cgt"
  | "specialty_drug"
  | "rheumatoid_arthritis"
  | "crohns_disease"
  | "psoriasis"
  | "other";

/** All possible QualifyingCondition values. Shared across endorsement and lasered claimant UIs. */
export const ALL_QUALIFYING_CONDITIONS: QualifyingCondition[] = [
  "kidney_failure",
  "cancer",
  "transplant",
  "cgt",
  "specialty_drug",
  "rheumatoid_arthritis",
  "crohns_disease",
  "psoriasis",
  "other",
];

// ─────────────────────────────────────────────────────────────
// Junction table row types
// ─────────────────────────────────────────────────────────────

/** Row from `tiered_deductible_tiers` junction table */
export interface TieredDeductibleTier {
  id: string;
  tier_order: number;
  deductible: number;
  age_range_start: number;
  age_range_end: number;
  reimbursement_pct: number;
}

/** Row from `admin_change_entries` junction table */
export interface AdminChangeEntry {
  id: string;
  admin_change_type: string;
  effective_date: string;
}

/** Row from `amendment_entries` junction table */
export interface AmendmentEntry {
  id: string;
  amendment_number: number;
  effective_date: string;
}

/** Row from `domestic_reimbursement_entries` junction table */
export interface DomesticReimbursementEntry {
  id: string;
  reason: string;
  adjustment_percentage: number;
  /** POS codes associated with this entry (from domestic_reimbursement_pos_codes) */
  pos_codes?: string[];
}

/**
 * Single endorsement item returned from `GET /policies/{id}/endorsements`.
 *
 * Key ERD mapping notes:
 * - valid_period_start/end map to DB daterange column
 * - codes map to junction tables (deductible_mod_icd_codes, deductible_mod_cpt_codes)
 * - qualifying_conditions maps to deductible_mod_qualifying_conditions
 *
 * TODO: replace `config` with a generic discriminated union once the API layer
 * is finalized. Target: `config: EndorsementConfigFor<T>` where T is the
 * endorsement type. For now, use `EndorsementConfigMap[EndorsementType]` in
 * typed contexts via the `EndorsementConfigFor` helper.
 */
export interface EndorsementListItem {
  id: string;
  policy_id: string;
  type: EndorsementType;
  label: string;
  is_enabled: boolean;
  is_currently_active: boolean;
  /** ISO date string (YYYY-MM-DD) — start of the DB daterange. Null if not configured. */
  valid_period_start: ISOString;
  /** ISO date string (YYYY-MM-DD) — end of the DB daterange. Null if not configured. */
  valid_period_end: ISOString;
  config: EndorsementConfigMap[EndorsementType] | null;
  version: number;

  // ── Junction sub-collections ──────────────────────────────
  /** ICD/CPT codes (deductible_modification) */
  codes?: EndorsementCodes;
  /** Qualifying conditions (deductible_modification) */
  qualifying_conditions?: QualifyingCondition[];
  /** Tier rows (tiered_deductible) */
  tiers?: TieredDeductibleTier[];
  /** Admin change entry rows (admin_changes) */
  admin_change_entries?: AdminChangeEntry[];
  /** Amendment entry rows (amendments) */
  amendment_entries?: AmendmentEntry[];
  /** Trigger type strings (contingent_terminal_liability) */
  trigger_types?: string[];
  /** Clinical trial phase strings (clinical_trials) */
  phases?: string[];
  /** Excluded ICD codes (exclusions, plan_mirroring, clinical_trials) */
  excluded_icd?: string[];
  /** Excluded CPT codes (plan_mirroring, transplant_coverage, reference_based_pricing) */
  excluded_cpt?: string[];
  /** NDC drug codes (specialty_drug) */
  ndc_codes?: string[];
  /** Excluded country codes (travel_medical) */
  excluded_countries?: string[];
  /** Excluded facility types (reference_based_pricing) */
  excluded_facilities?: string[];
  /** Credited claim IDs (provider_dispute_credit) */
  credited_claim_ids?: string[];
  /** Domestic reimbursement entries with nested POS codes (domestic_reimbursement_adjustments) */
  domestic_reimbursement_entries?: DomesticReimbursementEntry[];
}

export type EndorsementEventType = "activated" | "deactivated" | "modified" | "recorded";

/**
 * A single event in an endorsement's history timeline.
 */
export interface EndorsementEvent {
  id: string;
  endorsement_id: string;
  event_type: EndorsementEventType;
  reason: string;
  user: string;
  date: string;
  old_value?: unknown;
  new_value?: unknown;
}

/**
 * Frontend-only category bucket for grouping endorsement types.
 */
export interface EndorsementCategory {
  key: string;
  label: string;
  types: EndorsementType[];
}

// ─────────────────────────────────────────────────────────────
// Claimant deductible modification types (Workflow 3)
// ─────────────────────────────────────────────────────────────

/**
 * A single record from `GET /claimants/{id}/deductible-modifications`.
 *
 * Maps to `claimant_deductible_modifications` table.
 * Each row records a deductible change for a claimant under a specific policy,
 * triggered by a `deductible_modification` endorsement.
 */
export interface DeductibleHistoryItem {
  id: string;
  claimant_id_ref: string;
  /** Claimant first name (enriched by backend on endorsement-side queries) */
  claimant_first_name?: string;
  /** Claimant last name (enriched by backend on endorsement-side queries) */
  claimant_last_name?: string;
  policy_id_ref: string;
  /** The endorsement that triggered this modification */
  endorsement_id_ref: string;
  /** ISO date — start of effective period */
  effective_period_start: ISOString | null;
  /** ISO date — end of effective period */
  effective_period_end: ISOString | null;
  /** Is modification currently active */
  is_triggered: boolean;
  qualifying_condition: QualifyingCondition;
  /** ISO date — when condition was identified */
  trigger_date: string;
  /** Deductible before modification (dollars) */
  original_deductible: number;
  /** New calculated deductible (dollars) */
  modified_deductible: number;
  /** FK to vendors table — approved vendor UUID */
  approved_vendor_id_ref?: string;
  /** Vendor company name (enriched by backend) */
  approved_vendor_name?: string;
  /** ISO date — when carrier was notified */
  notification_date?: string;
  /** Carrier notification ID */
  notification_reference?: string;
  // Audit fields
  created_by?: string;
  created_datetime?: ISOString;
  modified_by?: string;
  modified_datetime?: ISOString;
}
